import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaStar } from 'react-icons/fa';
import { TailSpin } from 'react-loader-spinner';

function RateOrder() {
  const { orderId, positionGeohash, restaurantId } = useParams();
  const navigate = useNavigate();
  
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [hover, setHover] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [restaurantDetails, setRestaurantDetails] = useState(null);
  const [error, setError] = useState(null);
  const [ratingSubmitted, setRatingSubmitted] = useState(false);
  const [submittingRating, setSubmittingRating] = useState(false);
  const [orderSummary, setOrderSummary] = useState(null);
  const [alreadyRated, setAlreadyRated] = useState(false);
  const [existingRating, setExistingRating] = useState(0);
  const [loadingOrderSummary, setLoadingOrderSummary] = useState(true);

  useEffect(() => {
    // Fetch order summary first
    const fetchOrderSummary = async () => {
      try {
        const response = await axios.get(`/api/nexus/order/summary/${orderId}/v3`, {
          headers: {
            'accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });
        console.log("Order summary:", response.data);
        setOrderSummary(response.data);
        
        // Check if order is already rated
        if (response.data.ratingContext?.rated) {
          setAlreadyRated(true);
          setExistingRating(response.data.ratingContext.rating || 0);
        }
      } catch (error) {
        console.error('Error fetching order summary:', error);
        setError('Failed to load order details');
      } finally {
        setLoadingOrderSummary(false);
      }
    };

    if (orderId) {
      fetchOrderSummary();
    }
  }, [orderId]);

  useEffect(() => {
    // Fetch restaurant details
    const fetchRestaurantDetails = async () => {
      try {
        const response = await axios.get(`/api/catalogue/restaurant/${positionGeohash}/${restaurantId}/summary`, {
          headers: {
            'accept': 'application/json'
          }
        });
        console.log("Restaurant details:", response.data);
        setRestaurantDetails(response.data);
      } catch (error) {
        console.error('Error fetching restaurant details:', error);
        setError('Failed to load restaurant details');
      }
    };

    if (positionGeohash && restaurantId) {
      fetchRestaurantDetails();
    }
  }, [positionGeohash, restaurantId]);

  // Auto-submit rating when 5 stars is selected
  useEffect(() => {
    if (rating === 5 && !ratingSubmitted && restaurantDetails && !alreadyRated) {
      handleAutoSubmit();
    }
  }, [rating, ratingSubmitted, restaurantDetails, alreadyRated]);

  const handleAutoSubmit = async () => {
    setSubmittingRating(true);
    setError(null);

    try {
      const requestData = {
        type: "ORDER",
        orderId: orderId,
        rating: rating,
        feedback: "5-star rating - excellent experience"
      };

      const response = await axios.post('/api/nexus/order/rate', requestData, {
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        setRatingSubmitted(true);
      } else {
        setError('Failed to submit rating. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting rating:', error);
      setError('Failed to submit rating. Please try again.');
    } finally {
      setSubmittingRating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (rating === 0) {
      setError('Please select a rating');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const requestData = {
        type: "ORDER",
        orderId: orderId,
        rating: rating,
        feedback: feedback.trim() || "No feedback provided"
      };

      const response = await axios.post('/api/nexus/order/rate', requestData, {
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        setSubmitted(true);
        // Redirect after 3 seconds
        setTimeout(() => {
          window.location.href = 'https://tavahot.com';
        }, 3000);
      } else {
        setError('Failed to submit rating. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting rating:', error);
      setError('Failed to submit rating. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleMapsRating = () => {
    if (restaurantDetails?.socialMedia?.GOOGLE_MAPS) {
      window.open(restaurantDetails.socialMedia.GOOGLE_MAPS, '_blank');
    }
    setSubmitted(true);
    // Redirect after 3 seconds
    setTimeout(() => {
      window.location.href = 'https://tavahot.com';
    }, 3000);
  };

  const skipGoogleMapsRating = () => {
    setSubmitted(true);
    // Redirect after 3 seconds
    setTimeout(() => {
      window.location.href = 'https://tavahot.com';
    }, 3000);
  };

  const getAlreadyRatedMessage = (rating) => {
    switch (rating) {
      case 1:
        return "We're truly sorry for falling short of your expectations. Your feedback helps us grow and improve. We're working hard to make things right.\n\n🙏";
      case 2:
        return "We hear you and we're taking your feedback seriously. Every experience matters to us, and we're committed to doing better.\n\n🌱";
      case 3:
        return "Thank you for sharing your thoughts with us. We're constantly learning and improving to serve you better. Your feedback is our roadmap to excellence.\n\n🚀";
      case 4:
        return "We're so happy you enjoyed your experience! Your satisfaction means the world to us. Thank you for choosing us!\n\n💫";
      case 5:
        return "You've made our day! We're overjoyed that we could create such a wonderful experience for you. Thank you for your amazing feedback!\n\n🎉";
      default:
        return "Thank you for taking the time to share your thoughts with us. Every piece of feedback helps us grow.\n\n💝";
    }
  };

  if (loadingOrderSummary) {
    return (
      <div className="pt-24 flex flex-col w-full h-full items-center bg-[#FBFCFF]">
        <div className="flex justify-center items-center">
          <TailSpin
            height="80"
            width="80"
            color="#FBC02D"
            ariaLabel="loading"
            radius="1"
            visible={true}
          />
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="pt-24 flex flex-col w-full h-full items-center bg-[#FBFCFF]">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="mb-6">
            <FaStar className="text-6xl text-yellow-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-[#171725] mb-2">Thank You!</h2>
            <p className="text-[#696974]">Your feedback has been submitted successfully.</p>
          </div>
          <p className="text-sm text-[#696974]">Redirecting you to Tavahot...</p>
        </div>
      </div>
    );
  }

  // Show already rated message
  if (alreadyRated) {
    return (
      <div className="pt-24 flex flex-col w-full h-full items-center bg-[#FBFCFF]">
        <div className="w-full max-w-md mx-auto px-4">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-[#171725] mb-2">Order Already Rated</h1>
            <p className="text-[#696974] mb-4">Thank you for your feedback!</p>
            {orderSummary && (
              <div className="mt-2">
                <p className="text-sm text-[#696974]">Restaurant: {orderSummary.restaurantName}</p>
                <p className="text-sm text-[#696974]">Order ID: {orderId}</p>
              </div>
            )}
          </div>

          <div className="text-center mb-6">
            <div className="flex justify-center space-x-2 mb-4">
              {[...Array(5)].map((_, index) => {
                const ratingValue = index + 1;
                return (
                  <FaStar
                    key={index}
                    className={`text-3xl ${
                      ratingValue <= existingRating
                        ? 'text-yellow-400'
                        : 'text-gray-300'
                    }`}
                  />
                );
              })}
            </div>
            <p className="text-sm text-[#696974] mb-4">
              Your rating: {existingRating} {existingRating === 1 ? 'star' : 'stars'}
            </p>
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-blue-800 text-sm whitespace-pre-line text-center">
                {getAlreadyRatedMessage(existingRating)}
              </p>
            </div>
          </div>

          <button
            onClick={() => window.location.href = 'https://tavahot.com'}
            className="w-full py-3 px-4 bg-[#FBC02D] text-white rounded-md font-medium hover:bg-[#F9A825] transition-colors"
          >
            Visit Tavahot
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 flex flex-col w-full h-full items-center bg-[#FBFCFF]">
      <div className="w-full max-w-md mx-auto px-4">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-[#171725] mb-2">Rate Your Order</h1>
          <p className="text-[#696974]">Help us improve by sharing your experience</p>
          {orderSummary && (
            <div className="mt-2">
              <p className="text-sm text-[#696974]">Restaurant: {orderSummary.restaurantName}</p>
              <p className="text-sm text-[#696974]">Order ID: {orderId}</p>
            </div>
          )}
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Rating Section */}
          <div>
            <label className="block text-sm font-medium text-[#171725] mb-3">
              How would you rate your experience? *
            </label>
            <div className="flex justify-center space-x-2">
              {[...Array(5)].map((_, index) => {
                const ratingValue = index + 1;
                return (
                  <FaStar
                    key={index}
                    className={`text-3xl cursor-pointer transition-colors ${
                      (hover || rating) >= ratingValue
                        ? 'text-yellow-400'
                        : 'text-gray-300'
                    } ${ratingSubmitted ? 'cursor-not-allowed' : ''}`}
                    onClick={() => !ratingSubmitted && setRating(ratingValue)}
                    onMouseEnter={() => !ratingSubmitted && setHover(ratingValue)}
                    onMouseLeave={() => !ratingSubmitted && setHover(null)}
                  />
                );
              })}
            </div>
            <div className="text-center mt-2">
              <span className="text-sm text-[#696974]">
                {rating === 1 && 'Poor'}
                {rating === 2 && 'Fair'}
                {rating === 3 && 'Good'}
                {rating === 4 && 'Very Good'}
                {rating === 5 && 'Excellent'}
              </span>
            </div>
            {submittingRating && (
              <div className="text-center mt-2">
                <span className="text-sm text-[#696974]">Submitting your rating...</span>
              </div>
            )}
          </div>

          {/* Feedback Section - Only show when rating is below 5 stars */}
          {rating > 0 && rating < 5 && (
            <div>
              <label htmlFor="feedback" className="block text-sm font-medium text-[#171725] mb-2">
                {rating === 1 && "We are very sorry, please write down what went wrong."}
                {rating === 2 && "We apologize for the inconvenience. Please share what we can improve."}
                {rating === 3 && "Thank you for your feedback. Please tell us how we can serve you better."}
                {rating === 4 && "We're glad you had a good experience! Please share any suggestions for improvement."}
              </label>
              <textarea
                id="feedback"
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Share your thoughts about the food, service, or anything else..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent resize-none"
                rows="4"
                maxLength="500"
              />
              <div className="text-right mt-1">
                <span className="text-xs text-[#696974]">
                  {feedback.length}/500
                </span>
              </div>
            </div>
          )}

          {/* Success Message for 5-star rating */}
          {rating === 5 && ratingSubmitted && (
            <div className="text-center p-4 bg-green-50 border border-green-200 rounded-md">
              <p className="text-green-800 font-medium">
                Glad! We could please you with our service! 🎉
              </p>
            </div>
          )}
        </form>

        {/* Submit Button or Google Maps Button */}
        <div className="mt-6">
          {rating === 5 && ratingSubmitted ? (
            // Google Maps button for 5-star ratings
            <div className="space-y-4">
              <button
                onClick={handleGoogleMapsRating}
                className="w-full py-3 px-4 bg-[#FBC02D] text-white rounded-md font-medium hover:bg-[#F9A825] transition-colors"
              >
                Rate us on Google Maps
              </button>
              <button
                onClick={skipGoogleMapsRating}
                className="w-full py-3 px-4 bg-gray-300 text-gray-700 rounded-md font-medium hover:bg-gray-400 transition-colors"
              >
                Skip for now
              </button>
            </div>
          ) : (
            // Regular submit button for other ratings
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading || rating === 0}
              className={`w-full py-3 px-4 rounded-md font-medium transition-colors ${
                loading || rating === 0
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-[#FBC02D] text-white hover:bg-[#F9A825] active:bg-[#F9A825]'
              }`}
              style={{
                backgroundColor: loading || rating === 0 ? '#D1D5DB' : '#FBC02D',
                color: loading || rating === 0 ? '#6B7280' : '#FFFFFF'
              }}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <TailSpin
                    height="20"
                    width="20"
                    color="#ffffff"
                    ariaLabel="loading"
                    radius="1"
                    visible={true}
                  />
                  <span className="ml-2">Submitting...</span>
                </div>
              ) : (
                'Submit Rating'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default RateOrder; 